/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useState } from "react";

/* ----------------------------- base32 ----------------------------- */

const B32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

function base32Encode(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = "";
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += B32[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += B32[(value << (5 - bits)) & 31];
  return out;
}

function base32Decode(str: string): Uint8Array {
  const clean = str.replace(/=+$/, "").toUpperCase().replace(/\s/g, "");
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const c of clean) {
    const idx = B32.indexOf(c);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return Uint8Array.from(out);
}

/* ------------------------------ TOTP ------------------------------ */

async function hmacSha1(key: Uint8Array, msg: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, msg as BufferSource);
  return new Uint8Array(sig);
}

async function generateTotp(secretBase32: string, timeStep = 30): Promise<string> {
  const secret = base32Decode(secretBase32);
  const counter = Math.floor(Date.now() / 1000 / timeStep);
  const buf = new Uint8Array(8);
  let c = counter;
  for (let i = 7; i >= 0; i--) {
    buf[i] = c & 0xff;
    c = Math.floor(c / 256);
  }
  const hash = await hmacSha1(secret, buf);
  const offset = hash[hash.length - 1] & 0xf;
  const code =
    ((hash[offset] & 0x7f) << 24) |
    ((hash[offset + 1] & 0xff) << 16) |
    ((hash[offset + 2] & 0xff) << 8) |
    (hash[offset + 3] & 0xff);
  const str = (code % 1_000_000).toString().padStart(6, "0");
  return str;
}

export function useTotp(secret: string): {
  code: string;
  secondsLeft: number;
  verify: (input: string) => Promise<boolean>;
  generateSecret: () => string;
} {
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(30);

  useEffect(() => {
    if (!secret) {
      setCode("");
      return;
    }
    let timer = 0;
    const update = (): void => {
      const step = 30;
      const elapsed = Math.floor(Date.now() / 1000) % step;
      setSecondsLeft(step - elapsed);
      void generateTotp(secret).then(setCode);
    };
    update();
    timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [secret]);

  const verify = useCallback(
    async (input: string) => {
      if (!secret) return false;
      const current = await generateTotp(secret);
      // allow previous window (clock skew)
      const prevCounter = Math.floor(Date.now() / 1000 / 30) - 1;
      const prevBuf = new Uint8Array(8);
      let c = prevCounter;
      for (let i = 7; i >= 0; i--) {
        prevBuf[i] = c & 0xff;
        c = Math.floor(c / 256);
      }
      const prevHash = await hmacSha1(base32Decode(secret), prevBuf);
      const offset = prevHash[prevHash.length - 1] & 0xf;
      const codeNum =
        ((prevHash[offset] & 0x7f) << 24) |
        ((prevHash[offset + 1] & 0xff) << 16) |
        ((prevHash[offset + 2] & 0xff) << 8) |
        (prevHash[offset + 3] & 0xff);
      const prevCode = (codeNum % 1_000_000).toString().padStart(6, "0");
      return input === current || input === prevCode;
    },
    [secret],
  );

  const generateSecret = useCallback((): string => {
    const rand = new Uint8Array(20);
    crypto.getRandomValues(rand);
    return base32Encode(rand);
  }, []);

  return { code, secondsLeft, verify, generateSecret };
}
